import { useEffect, useState } from "react";
import {
  Loader2,
  MapPin,
  Timer,
  Puzzle,
  Users2,
  Trophy,
  Smartphone,
} from "lucide-react";
import {
  registerForEvent,
  registerTeamForEvent,
  type PastTeamSummary,
} from "@/api/registrations";
import { isVirtualEvent, type UpcomingEvent } from "@/api/eventDiscovery";
import { sendFormEmail } from "@/lib/email";
import { trackRegistrationCompleted } from "@/lib/analytics";
import { acceptCurrentTerms, hasAcceptedCurrentTerms } from "@/api/terms";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatMoney, pwywSymbol } from "@/lib/currency";
import { TeamChooser } from "./TeamChooser";
import { useTeamChoice } from "./useTeamChoice";
import type { OnAction, RegisterMode } from "./types";
import { ProfileGate } from "./ProfileGate";
import { useProfileGate } from "./useProfileGate";
import {
  updateMyProfile,
  type Gender,
  type PersonProfileFields,
} from "@/api/profile";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

interface Props {
  event: UpcomingEvent;
  pastTeams: PastTeamSummary[];
  /** Show the first-event-free reassurance only for people with no prior registrations. */
  isNewcomer: boolean;
  /** Current profile — used to gate registration until name (Latin) + gender are set. */
  personProfile: PersonProfileFields | null;
  /** Re-fetch after the inline profile fields save. */
  onProfileSaved: () => void;
  busy: boolean;
  onAction: OnAction;
  /** Collapse the form (the paired Cancel button). */
  onCancel: () => void;
}

/** Sets expectations at the point of doubt — fills the otherwise near-empty
 *  register screen and cuts "wait, what actually happens?" drop-off. Copy swaps
 *  for the two formats (onsite vs run-anywhere virtual). */
export function WhatToExpect({ isVirtual }: { isVirtual: boolean }) {
  const rows = isVirtual
    ? [
        {
          Icon: Smartphone,
          text: "Run any 5 km, any time — track it with a phone or watch",
        },
        { Icon: Puzzle, text: "10 lateral-thinking puzzles unlock as you go" },
        { Icon: Users2, text: "Solo or a team of 2–4 — your call" },
        {
          Icon: Timer,
          text: "Right answers cut your time — submit before the window closes",
        },
      ]
    : [
        { Icon: MapPin, text: "Meet 10 minutes before the start" },
        {
          Icon: Puzzle,
          text: "5 km run · 10 lateral-thinking puzzles along the way",
        },
        { Icon: Users2, text: "Solo or a team of 2–4 — your call" },
        {
          Icon: Trophy,
          text: "Right answers cut your time — fastest brain wins",
        },
      ];

  return (
    <div className="rounded-lg border bg-muted/30 p-3">
      <div className="mb-2 text-sm font-semibold">
        What to expect{isVirtual ? " — virtual" : ""}
      </div>
      <ul className="space-y-1.5">
        {rows.map(({ Icon, text }) => (
          <li
            key={text}
            className="flex items-start gap-2 text-sm text-muted-foreground"
          >
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>{text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function NotRegisteredView({
  event,
  pastTeams,
  isNewcomer,
  personProfile,
  onProfileSaved,
  busy,
  onAction,
  onCancel,
}: Props) {
  const { toast } = useToast();
  const [mode, setMode] = useState<RegisterMode>("solo");
  const {
    choice: teamChoice,
    setChoice: setTeamChoice,
    canSubmit: teamValid,
  } = useTeamChoice(pastTeams);

  // Inline profile fields live here so the single Register action saves them
  // then registers. Location is prefilled from the event (null for virtual).
  const isVirtual = isVirtualEvent(event);
  const gate = useProfileGate(
    personProfile,
    isNewcomer,
    isVirtual ? null : event.city,
    isVirtual ? null : event.country,
  );
  const gateActive = !!personProfile && gate.visible;

  // Terms consent is folded into the register act: record acceptance as part of
  // submit (no separate step).
  const [needsTerms, setNeedsTerms] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [teamError, setTeamError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    hasAcceptedCurrentTerms()
      .then((ok) => !cancelled && setNeedsTerms(!ok))
      .catch(() => !cancelled && setNeedsTerms(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRegister = async () => {
    const trackDone = (category: "solo" | "team") =>
      trackRegistrationCompleted({
        event_id: event.event_id,
        quizrun_number: event.quizrun_number,
        city: event.city,
        category,
      });

    const sendConfirmation = async () => {
      const result = await sendFormEmail({
        formType: "registration_confirmation",
        data: { event_id: String(event.event_id) },
      });
      if (!result.success) {
        logger.error("Registration confirmation email failed", { error: result.error });
        toast({
          title: "Registered, but confirmation email failed",
          description:
            "Your registration is saved. You can view event details in your profile.",
          variant: "destructive",
        });
      }
    };

    // Register is always clickable; validate here and surface inline messages
    // on whatever is missing instead of silently disabling the button.
    let blocked = false;
    if (gateActive && !gate.valid) {
      gate.setShowErrors(true);
      blocked = true;
    }
    if (mode === "team" && !teamValid) {
      setTeamError(true);
      blocked = true;
    }
    if (needsTerms && !termsChecked) {
      setTermsError(true);
      blocked = true;
    }
    if (blocked) return;

    // Persist profile first when the inline fields are shown.
    if (gateActive && personProfile) {
      const city = gate.value.homeCity.trim() || null;
      const saved = await onAction(
        () =>
          updateMyProfile(personProfile.id, {
            firstName: gate.value.firstName.trim(),
            lastName: gate.value.lastName.trim(),
            gender: gate.value.gender as Gender,
            homeCity: city,
            homeCountry: city ? gate.value.homeCountry : null,
            phone: gate.value.phone.trim() || null,
          }),
        "",
        "Couldn't save your details",
        "Please try again.",
      );
      if (!saved) return;
      onProfileSaved();
    }

    if (needsTerms) {
      await acceptCurrentTerms(true);
      setNeedsTerms(false);
    }

    if (mode === "solo") {
      const ok = await onAction(
        () => registerForEvent(event.event_id),
        `You're in — see you at ${event.city}!`,
        "Registration failed",
        "Couldn't register. Please try again.",
      );
      if (ok) {
        trackDone("solo");
        sendConfirmation();
      }
      return;
    }
    if (!teamValid) return;
    const payload =
      teamChoice.kind === "existing"
        ? { teamId: teamChoice.teamId }
        : { newTeamName: teamChoice.name.trim() };
    const ok = await onAction(
      () => registerTeamForEvent(event.event_id, payload),
      "Team registered!",
      "Registration failed",
      "Couldn't register the team.",
    );
    if (ok) {
      trackDone("team");
      sendConfirmation();
    }
  };

  return (
    <div className="space-y-4">
      <WhatToExpect isVirtual={isVirtual} />

      <RadioGroup
        value={mode}
        onValueChange={(v) => setMode(v as RegisterMode)}
        className="grid gap-2"
      >
        <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/50">
          <RadioGroupItem value="solo" id="reg-solo" className="mt-0.5" />
          <div>
            <div className="font-medium">Solo</div>
            <div className="text-sm text-muted-foreground">
              Register just yourself. You can switch to a team later.
            </div>
          </div>
        </label>
        <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/50">
          <RadioGroupItem value="team" id="reg-team" className="mt-0.5" />
          <div className="flex-1">
            <div className="font-medium">Team</div>
            <div className="text-sm text-muted-foreground">
              Register a team. You'll be added automatically.
            </div>
          </div>
        </label>
      </RadioGroup>

      {mode === "team" && (
        <div className="rounded-md border bg-muted/30 p-3">
          <TeamChooser
            pastTeams={pastTeams}
            value={teamChoice}
            onChange={(next) => {
              setTeamChoice(next);
              if (teamError) setTeamError(false);
            }}
            idPrefix="reg"
            showLabel
            error={
              teamError
                ? "Please enter a team name (at least 2 letters)."
                : null
            }
          />
        </div>
      )}

      {/* Profile fields sit below the Solo/Team choice; saved as part of the
          single Register action (no separate Save button). */}
      {gateActive ? <ProfileGate gate={gate} /> : null}

      {/* Price reminder + payment nudge at the point of doubt. We WANT early
          payment, so the ask is 'pay anytime before the start' — never 'on the
          day'. Newcomers: first regular event free (donation only). Others: show
          the real price / PWYW range + arrive-early note. */}
      {isNewcomer ? (
        <p className="text-xs text-muted-foreground">
          New here? Your first regular event's on us — no payment needed. If you
          enjoy it, a small donation or a coffee is always welcome. ☕
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {event.price_cents != null && event.price_cents > 0
            ? formatMoney(event.price_cents, event.price_currency ?? null)
            : `${pwywSymbol(event.price_currency)}5–20+ — pay what you want`}
          . Register now and pay anytime before the start.
        </p>
      )}

      <div className="flex flex-col gap-3">
        {needsTerms && (
          <div>
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <Checkbox
                checked={termsChecked}
                onCheckedChange={(v) => {
                  setTermsChecked(v === true);
                  if (v === true) setTermsError(false);
                }}
                className="mt-0.5"
                aria-label="Accept the Terms & Conditions and Liability Waiver"
              />
              <span>
                I accept the{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline underline-offset-2 hover:text-foreground"
                >
                  Terms &amp; Waiver
                </Link>
              </span>
            </label>
            {termsError ? (
              <p className="mt-1 text-xs text-destructive">
                Please accept the Terms &amp; Waiver to register.
              </p>
            ) : null}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleRegister} disabled={busy} aria-busy={busy}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Register
          </Button>
        </div>
      </div>
    </div>
  );
}
